import { useNavigate, useParams } from 'react-router-dom'

import { Box, Spinner, Text } from '@factorialco/deprecated-design-system'
import { DetailsItemType, DetailsItemsList } from '@factorialco/f0-react/dist/experimental'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import ApprovalsInformationBlock from 'modules/approvals/components/ApprovalsInformationBlock'
import { RequisitionStatusTag } from 'modules/ats/components/RequisitionStatusTag'
import { useRequisition } from 'modules/ats/hooks/requisitions/useRequisition'

import { RequestActions } from './RequestActions'

const JobRequisitionDetailPage = () => {
  const params = useParams()
  const requisitionId = Number(params.requisition_id)
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { loading, requisition } = useRequisition(requisitionId)
  const data: DetailsItemType[] = []

  const {
    access: { id: currentAccessId },
  } = useCurrentSession()

  const showFooterActions = () => {
    if (loading) {
      return false
    }
    const isApprover = requisition.currentApproverIds?.includes(currentAccessId)
    const isAuthorOrApprover = requisition.author.id == currentAccessId || isApprover

    return requisition.status === 'pending' && isAuthorOrApprover
  }

  const onClose = () => {
    navigate(navigation.ats.jobs.requisitions.fullPath())
  }

  if (!!requisition) {
    data.push({
      content: {
        text: requisition.jobPostingTitle,
        type: 'item',
      },
      title: i18n.t('ats.jobs_requisitions.requisition_detail.job_opening_title'),
    })
    if (requisition.team)
      data.push({
        content: [
          {
            action: {
              type: 'navigate',
              href: navigation.employees.teams.id.fullPath({ id: requisition.team.id }),
            },
            type: 'team',
            name: requisition.team.name,
          },
        ],
        title: i18n.t('ats.jobs_requisitions.requisition_detail.team'),
      })

    data.push(
      {
        content: {
          text: requisition.workplace?.city ?? '',
          type: 'item',
        },
        title: i18n.t('ats.jobs_requisitions.requisition_detail.workplace'),
      },

      {
        content: {
          text:
            requisition.reason === 'new_position'
              ? i18n.t('ats.jobs_requisitions.requisition_detail.reasons.new_position')
              : i18n.t('ats.jobs_requisitions.requisition_detail.reasons.backfill'),
          type: 'item',
        },
        title: i18n.t('ats.jobs_requisitions.requisition_detail.reason'),
      }
    )
    if (requisition.hiringManager)
      data.push({
        content: {
          action: {
            type: 'navigate',
            href: navigation.employees.id.fullPath({ id: requisition.hiringManager.id }),
          },
          type: 'person',
          avatarUrl: buildUrl(requisition.hiringManager.avatar ?? ''),
          firstName: requisition.hiringManager.fullName ?? '',
          lastName: '',
        },
        title: i18n.t('ats.jobs_requisitions.requisition_detail.hiring_manager'),
      })

    if (requisition.recruiter)
      data.push({
        content: {
          action: {
            type: 'navigate',
            href: navigation.employees.id.fullPath({ id: requisition.recruiter.id }),
          },
          type: 'person',
          avatarUrl: buildUrl(requisition.recruiter.avatar ?? ''),
          firstName: requisition.recruiter.fullName ?? '',
          lastName: '',
        },
        title: i18n.t('ats.jobs_requisitions.requisition_detail.process_owner'),
      })

    if (requisition.note)
      data.push({
        isHorizontal: false,
        spacingAtTheBottom: true,
        content: {
          text: requisition.note,
          type: 'item',
        },
        title: i18n.t('ats.jobs_requisitions.requisition_detail.note'),
      })

    if (requisition.status === 'rejected' && requisition.materializedApprovalFlow)
      data.push({
        isHorizontal: false,
        spacingAtTheBottom: true,
        content: {
          text: requisition.materializedApprovalFlow.reason ?? '',
          type: 'item',
        },
        title: i18n.t('ats.jobs_requisitions.requisition_detail.rejection_reason'),
      })
  }

  return (
    <Sidepanel
      title={
        <Box alignItems="center" justifyContent="center" flexDirection="row" flexGrow gap="s12">
          <Text.Normal weight="semibold">
            {i18n.t('ats.jobs_requisitions.requisition_detail.title')}
          </Text.Normal>
          {!!requisition?.status && <RequisitionStatusTag status={requisition.status} />}
        </Box>
      }
      onClose={onClose}
      width="wide"
      closeLabel={i18n.t('ats.jobs_requisitions.requisition_detail.close')}
      footer={showFooterActions() && <RequestActions requisition={requisition} />}
      boxPadding
      open
    >
      <Box height="full">
        {loading ? (
          <Box alignItems="center">
            <Spinner />
          </Box>
        ) : (
          <Box flexGrow gap="s8">
            <Box paddingBottom="s24">
              <ApprovalsInformationBlock
                resourceId={requisition.id}
                resourceType="Ats::Requisition"
              />
            </Box>
            <DetailsItemsList
              details={data}
              title={i18n.t('ats.jobs_requisitions.requisition_detail.details')}
              tableView
            />
          </Box>
        )}
      </Box>
    </Sidepanel>
  )
}

export default JobRequisitionDetailPage
